<template>
  <StreamInsightsChart
    v-if="selectedStream"
    :authors="authors"
    :chart-config="chartConfig"
    :chart-setting="chartSetting"
    :collapse-syndicated="collapseSyndicated"
    :debounce="debounce"
    :disable-loading="disableLoading"
    :filter-set-id="filterSetId"
    :media="media"
    :range="range"
    :selected-stream="selectedStream"
    @data-loaded="updateChartConfig"
  />
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

import type DateRange from "shared/helpers/DateRange";
import { randomUUID } from "shared/helpers/uuid";
import StreamInsightsChart from "shared/modules/Charts/components/StreamInsightsChart/StreamInsightsChart.vue";
import { type BaseSavedChartSetting } from "shared/modules/Charts/types";
import { type ChartConfig } from "shared/modules/Charts/types/chartConfig";
import {
  type ChartLibraryProps,
  type MentionVolumeData,
} from "shared/modules/Charts/types/data";
import { type Nullable, type Stream } from "shared/types";

interface Props {
  authors?: Array<any>;
  collapseSyndicated?: boolean;
  debounce?: number;
  disableLoading?: boolean;
  filterSetId?: Nullable<number>;
  height?: string;
  media: Array<any>;
  range: InstanceType<typeof DateRange>;
  selectedStream: Nullable<Stream>;
}

const props = withDefaults(defineProps<Props>(), {
  authors: () => [],
  debounce: 0,
  filterSetId: null,
  height: "110px",
});

defineEmits<{
  reload: [];
}>();

const chartSetting = ref<BaseSavedChartSetting>({
  chart: "coverage_volume_timeline",
  uuid: randomUUID(),
  range: props.range,
  notes: "",
});

const chartConfig = ref<ChartConfig>({
  component: "CoverageVolumeTimeline",
  method: "coverage_volume_timeline",
  options: {
    show_title: false,
    hide_legend: true,
  },
  chartOptions: {
    scales: {
      x: {
        grid: {
          display: true,
          drawTicks: false,
          color: "#D4DAE0",
        },
        ticks: {
          display: false,
        },
      },
      volume: {
        display: false,
      },
    },
  },
  style: {
    height: props.height,
    padding: "6px 50px 6px 40px",
  },
});

watch(
  () => props.range,
  (range: DateRange) => {
    chartSetting.value.range = range;
  }
);

function updateChartConfig(chartData: ChartLibraryProps): void {
  const data = chartData.data as MentionVolumeData;
  const hasData = data?.datasets?.some((dataset) => dataset.data?.length);

  if (chartConfig.value.style) {
    if (hasData) {
      chartConfig.value.style.padding = "0";
    } else {
      chartConfig.value.style.padding = "6px 50px 6px 40px";
    }
  }
}
</script>
